import { KeyboardEvent, Ref, useRef, useState } from "react";
import Autosuggest, {
  ChangeEvent as AutosuggestChangeEvent,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
  SuggestionSelectedEventData,
  SuggestionsFetchRequestedParams,
} from "react-autosuggest";
import { useTranslation } from "react-i18next";
import TextInput from "components/ui/TextInput";
import { getPreviousSearch } from "services/dictionary";
import SearchIcon from "images/search.svg?react";
import DropdownIcon from "./images/dropdown.svg?react";
import "./search.css";
import "./SearchInputWithSuggestions.css";

const SEARCH_PLACEHOLDER = "Dictionary.search.placeholder";
const MAX_SEARCH_LENGTH = 512;

type FocusableInput = {
  focus: () => void;
  blur: () => void;
};

type Props = {
  value: string;
  onValueChanged: (value: string) => void;
  onSearch: (value?: string) => void;
};

export default function SearchInputWithSuggestions(props: Props) {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState<string[]>(getPreviousSearch);
  const [isSuggestionsContainerOpened, setIsSuggestionsContainerOpened] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const searchInputRef = useRef<FocusableInput | null>(null);

  const getSuggestions = (input: string) => {
    const history = getPreviousSearch();
    const inputValue = input.trim().toLowerCase();
    if (inputValue.length === 0) {
      return history;
    }
    return history.filter(
      suggestion => suggestion.toLowerCase().slice(0, inputValue.length) === inputValue
    );
  };

  const onChange = (_: unknown, { newValue }: AutosuggestChangeEvent) => {
    props.onValueChanged(newValue);
    if (!isSuggestionsContainerOpened) {
      setIsSuggestionsContainerOpened(true);
    }
  };

  const onSuggestionsFetchRequested = ({
    value: input,
  }: SuggestionsFetchRequestedParams) => {
    setSuggestions(getSuggestions(input));
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onArrowClick = () => {
    const shouldBeOpened = !isSuggestionsContainerOpened;
    setIsSuggestionsContainerOpened(shouldBeOpened);
    setSuggestions(getSuggestions(props.value));
    if (shouldBeOpened) {
      searchInputRef.current?.focus();
    } else {
      searchInputRef.current?.blur();
    }
  };

  const closeSuggestions = () => {
    setIsSuggestionsContainerOpened(false);
    setIsFocused(false);
  };

  const onSearchClick = () => {
    closeSuggestions();
    props.onSearch();
  };

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      // Proceed to search after click on Enter.
      onSearchClick();
    }
  };

  const onSuggestionSelected = (
    _: unknown,
    { suggestionValue }: SuggestionSelectedEventData<string>
  ) => {
    if (suggestionValue) {
      closeSuggestions();
      props.onSearch(suggestionValue);
    }
  };

  // The lookup pill is the kit ui/TextInput; Autosuggest still owns the input's
  // props and needs a handle on the real control, which TextInput forwards as
  // its ref. The history-dropdown and search actions ride in the pill's
  // trailing slot. Autosuggest's native `size` is dropped: the kit reads that
  // prop as a control height, not a character count.
  const renderInputComponent = (inputProps: RenderInputComponentProps) => {
    const {
      key: inputKey,
      ref: inputRef,
      size: _nativeSize,
      ...restInputProps
    } = inputProps as RenderInputComponentProps & { key?: string };

    return (
      <TextInput
        variant="pill"
        key={inputKey}
        ref={inputRef as Ref<HTMLInputElement>}
        {...restInputProps}
        maxLength={MAX_SEARCH_LENGTH}
        onKeyPress={onKeyPress}
        trailing={
          <span className="input-actions-container">
            <button
              type="button"
              className="dropdown-btn talaera-field-action"
              aria-label={t("Dictionary.search.recentSearches")}
              title={t("Dictionary.search.recentSearches")}
              onClick={onArrowClick}
            >
              <DropdownIcon aria-hidden />
            </button>
            <span className="talaera-field-divider" aria-hidden />
            <button
              type="button"
              className="search-btn talaera-field-action"
              aria-label={t("Common.actions.search")}
              title={t("Common.actions.search")}
              onClick={onSearchClick}
            >
              <SearchIcon aria-hidden />
            </button>
          </span>
        }
      />
    );
  };

  const renderSuggestionsContainer = ({
    containerProps,
    children,
  }: RenderSuggestionsContainerParams) => {
    if (!isSuggestionsContainerOpened && !isFocused) {
      return null;
    }
    return <div {...containerProps}>{children}</div>;
  };

  const storeInputReference = (autosuggest: Autosuggest<string> | null) => {
    if (autosuggest !== null) {
      searchInputRef.current = (autosuggest.input as unknown as FocusableInput) ?? null;
    }
  };

  return (
    <Autosuggest
      suggestions={suggestions}
      alwaysRenderSuggestions={true}
      highlightFirstSuggestion={true}
      getSuggestionValue={suggestion => suggestion}
      focusInputOnSuggestionClick={false}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      onSuggestionSelected={onSuggestionSelected}
      renderSuggestion={suggestion => <div className="suggestion">{suggestion}</div>}
      renderInputComponent={renderInputComponent}
      renderSuggestionsContainer={renderSuggestionsContainer}
      inputProps={{
        value: props.value,
        onChange,
        onFocus: () => setIsFocused(true),
        onBlur: () => setIsFocused(false),
        placeholder: t(SEARCH_PLACEHOLDER),
      }}
      ref={storeInputReference}
    />
  );
}
